import { Component, OnInit, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import * as _ from 'lodash';
import { ReplaySubject } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { copilotToAgentTypeMapping } from 'src/app/common/vertical-filters-utils';
import { debounceTime, tap, switchMap, finalize, distinctUntilChanged, filter, take } from 'rxjs/operators';
import { AutocompleteSearchResultsModel } from 'src/app/models/api/autocompleteSearchResults.model';
import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { ViolationsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/violationsFilters.model';
import { ISelectOption } from 'src/app/models/selectOptions';
import { ISelectOptionGroups } from 'src/app/models/selectOptions/selectOptionGroups.interface';
import { InventoryHttpService } from 'src/app/services/httpServices/inventoryHttpService/inventory-http.service';
import { AggregationsHttpServiceService } from 'src/app/services/httpServices/aggregationsHttpService/aggregations-http-service.service';
import { createDepartmentVerticalFilter, initDepartmentFilter } from 'src/app/common/departmentFilterUtils';
import { LabelModel } from 'src/app/models/api/common';
import { StoreService } from 'src/app/services/infra/store/store.service';
@Component({
  selector: 'app-violations-vertical-filters',
  templateUrl: './violations-vertical-filters.component.html',
  styleUrls: ['./violations-vertical-filters.component.scss'],
})
export class ViolationsVerticalFiltersComponent implements OnInit {
  @Input() public pageFilters: ViolationsFiltersModel;
  @Input() public withCategories = true;
  @Input() public isFiltersMenuOpen: boolean;
  @Output() public filtersOnMenuStateChange = new EventEmitter<boolean>();

  @Output() public optionOnSelection = new EventEmitter<ISelectOption[]>();

  public optionsInsideCategories$: BehaviorSubject<ISelectOptionGroups[]> = new BehaviorSubject<ISelectOptionGroups[]>(
    []
  );
  public expandedCategories: { [optionGroup: string]: boolean } = {};
  public selectedIds: Set<string>;
  public isOpen = true;

  public isFiltersReady$ = new BehaviorSubject<boolean>(false); // We need to wait until agent/copilot duplicates were removed from the filters

  public selectedOptions: ISelectOption[] = [];

  public searchLabelNameValues: string[] = [];
  public allLabels: LabelModel[] = [];
  public searchLabelShown: ISelectOption[] = [];
  public searchLabelShownHeight = '0px';

  public searchEnvironmentNameValuesCtrl = new UntypedFormControl();
  public filteredEnvironmentNameValues: ISelectOption[] = [];
  public isEnvironmentNameLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public environmentNameFilterEnum = FilterTypesEnum.Environment;
  public searchEnvironmentNameNewValue = new ReplaySubject<string[] | undefined>(1);

  public searchCreatedByValuesCtrl = new UntypedFormControl();
  public filteredCreatedByValues: ISelectOption[] = [];
  public isCreatedByLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public createdByFilterEnum = FilterTypesEnum.CreatedBy;
  public searchCreatedByNewValue = new ReplaySubject<string[] | undefined>(1);

  public searchOwnedByValuesCtrl = new UntypedFormControl();
  public filteredOwnedByValues: ISelectOption[] = [];
  public isOwnedByLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public ownedByFilterEnum = FilterTypesEnum.OwnedBy;
  public searchOwnedByNewValue = new ReplaySubject<string[] | undefined>(1);

  public departmentFilter = createDepartmentVerticalFilter();

  public dynamicCategories: string[];
  public newCategories: string[];

  constructor(
    private readonly _inventoryHttpService: InventoryHttpService,
    private readonly _aggregationsHttpService: AggregationsHttpServiceService,
    private readonly _store: StoreService
  ) {
    this._store.labels$.subscribe((labels: LabelModel[]) => {
      if (labels.length === 0) return;
      this._initLabelNameFilter(labels);
    });
  }

  ngOnInit(): void {
    this.dynamicCategories = this.pageFilters.getDynamicFilters();
    this.newCategories = this.pageFilters.getNewFilters();

    this.pageFilters
      .getAllFiltersOptions()
      .pipe(tap((options) => this._updateFiltersReadyState(options)))
      .subscribe((options) => {
        const optionsInsideCategories: ISelectOptionGroups[] = [];
        const indexByCategory: { [key: string]: number } = {};

        options.forEach((option) => {
          if (option.categoryName) {
            const isNew = this.newCategories.includes(option.categoryName);
            if (option.categoryName in indexByCategory) {
              optionsInsideCategories[indexByCategory[option.categoryName]].options.push(option);
            } else if (!this.dynamicCategories.includes(option.categoryName)) {
              optionsInsideCategories.push({
                selectedCount: 0,
                displayName: option.categoryName,
                options: [option],
                isDynamic: false,
                isNew: isNew,
              });
              indexByCategory[option.categoryName] = optionsInsideCategories.length - 1;
            } else {
              optionsInsideCategories.push({
                selectedCount: 0,
                displayName: option.categoryName,
                options: [option],
                isDynamic: true,
                isNew: isNew,
              });
            }
          } else {
            optionsInsideCategories.push({
              options: [option],
            } as ISelectOptionGroups);
          }
        });
        this.dynamicCategories.forEach((category) => {
          optionsInsideCategories.push({
            selectedCount: 0,
            displayName: category,
            options: [],
            isDynamic: true,
          });
        });

        this.optionsInsideCategories$.next(optionsInsideCategories);
      });

    this.pageFilters.onUpdate$.subscribe((data) => {
      const newSelectedIds: Set<string> = new Set();

      this.pageFilters.getAllSelectedOptions().forEach((option) => {
        newSelectedIds.add(option.id);
      });
      this.selectedIds = newSelectedIds;

      this.selectedOptions = this.pageFilters.getAllSelectedOptions();
      this.searchCreatedByNewValue.next(data.createdBy);
      this.searchOwnedByNewValue.next(data.ownedBy);
      this.searchEnvironmentNameNewValue.next(data.environment);
      this.departmentFilter.searchNewValue.next(data.department);
    });

    this._initEnvironmentNameFilter();
    this._initCreatedByFilter();
    this._initOwnedByFilter();
    initDepartmentFilter(this._aggregationsHttpService, this.departmentFilter);
  }

  public expandClicked(optionGroupDisplayName: string, status: boolean) {
    this.expandedCategories[optionGroupDisplayName] = status;
  }

  public onToggleClick() {
    const isAboutToOpenFilters = !this.isFiltersMenuOpen;

    if (isAboutToOpenFilters) {
      this.isFiltersReady$.next(false);

      this._checkCurrentFiltersForDuplicates();

      this.isFiltersReady$
        .pipe(
          filter((isReady) => isReady),
          take(1)
        )
        .subscribe();
    }

    this.filtersOnMenuStateChange.emit(isAboutToOpenFilters);
    this.isOpen = !this.isOpen;
  }

  public tablesFiltersChange(selectedOptions: ISelectOption[]) {
    this.pageFilters.updateFilterFromUserSelection(undefined, selectedOptions);
  }

  public labelNameSubmitted(labels: string[] = []): void {
    this.searchLabelShown = [];
    labels.forEach((label: string) => {
      const labelModel = this.allLabels.find((labelExist: LabelModel) => labelExist.displayName === label);
      if (labelModel) {
        this.searchLabelShown.push({
          id: labelModel.id,
          value: labelModel.displayName,
          displayName: labelModel.displayName,
        } as ISelectOption);
      }
    });
    this.searchLabelShownHeight = this._getSearchLabelShownHeight();
  }

  public environmentNameSubmitted(environments: ISelectOption[] = []): void {
    this.pageFilters.environmentFilters.updateOptions(
      _.union(this.pageFilters.environmentFilters.getOptions$().value, environments)
    );
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this.selectedOptions, environments));
  }

  public createdBySubmitted(users: ISelectOption[] = []): void {
    this.pageFilters.createdByFilters.updateOptions(
      _.union(this.pageFilters.createdByFilters.getOptions$().value, users)
    );
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this.selectedOptions, users));
  }

  public ownedBySubmitted(users: ISelectOption[] = []): void {
    this.pageFilters.ownedByFilters.updateOptions(_.union(this.pageFilters.ownedByFilters.getOptions$().value, users));
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this.selectedOptions, users));
  }

  public departmentSubmitted(departments: ISelectOption[] = []) {
    this.pageFilters.departmentFilters.updateOptions(
      _.union(this.pageFilters.departmentFilters.getOptions$().value, departments)
    );
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), departments));
  }

  public onSelect(id: string): void {
    if (!this.selectedIds.has(id)) {
      this.selectedIds.add(id);
    } else {
      this.selectedIds.delete(id);
    }
    this.optionOnSelection.emit(this._getSelectedOptions());
    this.pageFilters.updateFilterFromUserSelection(undefined, this._getSelectedOptions());
  }

  private _initLabelNameFilter(labels: LabelModel[]) {
    labels.forEach((label: LabelModel) => {
      if (this.allLabels.find((labelExist: LabelModel) => labelExist.id === label.id)) return;
      this.allLabels.push(label);
      this.searchLabelNameValues.push(label.displayName);
    });
    this.labelNameSubmitted(this.searchLabelNameValues);
  }

  private _getSearchLabelShownHeight(): string {
    const height = Math.min(this.searchLabelShown.length * 30, 210);
    return height + 'px';
  }

  private _initEnvironmentNameFilter() {
    this._initFilter(
      this.searchEnvironmentNameValuesCtrl,
      'environment',
      this.filteredEnvironmentNameValues,
      this.isEnvironmentNameLoading$,
      FilterTypesEnum.Environment
    );
  }

  private _initCreatedByFilter() {
    this._initFilter(
      this.searchCreatedByValuesCtrl,
      'user',
      this.filteredCreatedByValues,
      this.isCreatedByLoading$,
      FilterTypesEnum.CreatedBy
    );
  }

  private _initOwnedByFilter() {
    this._initFilter(
      this.searchOwnedByValuesCtrl,
      'user',
      this.filteredOwnedByValues,
      this.isOwnedByLoading$,
      FilterTypesEnum.OwnedBy
    );
  }

  private _initFilter(
    control: UntypedFormControl,
    filter_name: string,
    filteredValues: ISelectOption[],
    loadingSubject: BehaviorSubject<boolean>,
    filterType: FilterTypesEnum
  ) {
    control.valueChanges
      .pipe(
        filter((res) => res !== null && res.length >= 3),
        distinctUntilChanged(),
        debounceTime(1000),
        tap(() => {
          filteredValues.splice(0, filteredValues.length);
          loadingSubject.next(true);
        }),
        switchMap((value) =>
          this._inventoryHttpService.getAutocompleteOptions(value, filter_name).pipe(
            finalize(() => {
              loadingSubject.next(false);
            })
          )
        )
      )
      .subscribe((data: AutocompleteSearchResultsModel[]) => {
        if (data.length === 0) {
          filteredValues.splice(0, filteredValues.length);
        } else {
          filteredValues.splice(
            0,
            filteredValues.length,
            ...data.map(
              (item) =>
                ({
                  id: item.id,
                  value: item.id,
                  displayName: item.displayName,
                  filterType: filterType,
                  categoryName: filterType,
                  withoutBeautifyDisplayName: true,
                }) as ISelectOption
            )
          );
        }
      });
  }

  private _updateFiltersReadyState(options: ISelectOption[]): void {
    const typeOptions = options.filter((opt) => opt.categoryName === 'Type');
    const hasDuplicates = this._detectAgentCopilotDuplicates(typeOptions);
    const isReady = !hasDuplicates;
    this.isFiltersReady$.next(isReady);
  }

  private _checkCurrentFiltersForDuplicates(): void {
    const currentOptions = this.optionsInsideCategories$.value || [];
    const allCurrentOptions: ISelectOption[] = [];

    currentOptions.forEach((group) => {
      if (group.options) {
        allCurrentOptions.push(...group.options);
      }
    });

    this._updateFiltersReadyState(allCurrentOptions);
  }

  private _detectAgentCopilotDuplicates(typeOptions: ISelectOption[]): boolean {
    const typeIds = typeOptions.map((opt) => opt.id);

    const hasDuplicates = Array.from(copilotToAgentTypeMapping.entries()).some(([copilot, agent]) => {
      const copilotId = `Type-${copilot}`;
      const agentId = `Type-${agent}`;
      return typeIds.includes(copilotId) && typeIds.includes(agentId);
    });

    return hasDuplicates;
  }

  private _getSelectedOptions(): ISelectOption[] {
    const selectedIndexArray = Array.from(this.selectedIds);
    const optionsById = _.keyBy(this.pageFilters.getAllFiltersOptions().value, 'id');

    /// This is needed because there are options that can be selected but are not shown.
    const selectedOptions = selectedIndexArray.map((id: string) => optionsById[id]);

    return selectedOptions.filter((options) => typeof options !== 'undefined');
  }
}
